import { useEffect, useMemo, useRef } from 'react';
import throttle from 'lodash/throttle';
import {
  getVisibleReadingLocator,
  getReadingProgress
} from '../utils/readingProgressUtils';

const SCROLL_DEBOUNCE_MS = 150;
const INITIAL_DETECT_DELAY_MS = 500;
const THROTTLE_MS = 2000;

export interface ScrollProgressUIState {
  progressPercent: number;
  currentEpisodeNumber: number;
  canScrollLeft: boolean;
  canScrollRight: boolean;
}

interface UseReadingProgressTrackerParams {
  scrollContainerRef: React.RefObject<HTMLDivElement>;
  sceneId: string | undefined;
  updateReadingProgress: (params: {
    sceneId: string;
    body: { episode_id: string; paragraph_index: number; char_offset: number };
  }) => void;
  episodes?: { sceneEpisodeId?: string }[];
  onProgressChange?: (state: ScrollProgressUIState) => void;
}

export const useReadingProgressTracker = ({
  scrollContainerRef,
  sceneId,
  updateReadingProgress,
  episodes = [],
  onProgressChange
}: UseReadingProgressTrackerParams) => {
  const lastSentKeyRef = useRef<string>('');
  const pendingLocatorRef = useRef<ReturnType<typeof getVisibleReadingLocator>>(null);
  const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const episodesRef = useRef(episodes);
  episodesRef.current = episodes;

  /** throttle된 API 전송. leading+trailing으로 스크롤 시작/끝 모두 반영 */
  const sendReadingProgress = useMemo(
    () =>
      throttle(
        () => {
          const loc = pendingLocatorRef.current;
          if (!loc || !sceneId) return;
          updateReadingProgress({
            sceneId,
            body: {
              episode_id: loc.episodeId,
              paragraph_index: loc.paragraphIndex,
              char_offset: loc.charOffset
            }
          });
        },
        THROTTLE_MS,
        { leading: true, trailing: true }
      ),
    [sceneId, updateReadingProgress]
  );

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const detectAndQueueProgress = () => {
      const loc = getVisibleReadingLocator(container);
      if (loc) {
        const key = `${loc.episodeId}-${loc.paragraphIndex}-${loc.charOffset}`;
        if (lastSentKeyRef.current !== key) {
          lastSentKeyRef.current = key;
          pendingLocatorRef.current = loc;
          sendReadingProgress();
        }
      }
      if (onProgressChange && episodesRef.current.length > 0) {
        const progress = getReadingProgress(container);
        const { scrollLeft, scrollWidth, clientWidth } = container;
        const canScrollLeft = scrollLeft > 2;
        const canScrollRight = scrollLeft < scrollWidth - clientWidth - 2;

        let progressPercent = 0;
        let currentEpisodeNumber = 1;

        if (progress && progress.totalChars > 0) {
          progressPercent = Math.min(
            100,
            Math.round(((progress.currentIndex + 1) / progress.totalChars) * 100)
          );
          if (progress.episodeId) {
            const idx = episodesRef.current.findIndex(
              (e) => e.sceneEpisodeId === progress.episodeId
            );
            if (idx >= 0) currentEpisodeNumber = idx + 1;
          }
        }

        onProgressChange({
          progressPercent,
          currentEpisodeNumber,
          canScrollLeft,
          canScrollRight
        });
      }
    };

    const onScroll = () => {
      clearTimeout(scrollTimeoutRef.current ?? undefined);
      scrollTimeoutRef.current = setTimeout(detectAndQueueProgress, SCROLL_DEBOUNCE_MS);
    };
    container.addEventListener('scroll', onScroll, { passive: true });
    const initTimer = setTimeout(detectAndQueueProgress, INITIAL_DETECT_DELAY_MS);
    return () => {
      container.removeEventListener('scroll', onScroll);
      if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
      clearTimeout(initTimer);
      sendReadingProgress.cancel();
    };
  }, [sendReadingProgress, scrollContainerRef, onProgressChange]);
};
